<template>
  <tabs />
  <div class="main-area h-full flex flex-col">
    <div class="flex gap-4 p-2">
      <Multiselect
        v-model="region"
        :options="regions"
        :loading="loadingRegions"
        :label="$t('visibility.planning.region')"
        class="min-w-[200px] p-2 max-w-[400px] w-[300px]"
        :can-deselect="false"
        :can-clear="true"
        :searchable="true"
      />
    </div>
    <v-chart
      v-if="!loading"
      ref="chartRef"
      :option="chartOptions"
      class="w-full h-full pr-0"
    />
    <div
      v-else
      style="display: flex; justify-content: center; align-items: center; padding: 10rem; margin-left: 3rem;"
    >
      <Spinner />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  ref, computed, unref, reactive,
} from 'vue';
import { use } from 'echarts/core';
import VChart from 'vue-echarts';
import { CustomChart, HeatmapChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  VisualMapComponent,
  CalendarComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import Multiselect from '@utils/Multiselect.vue';
import Spinner from '@utils/Spinner.vue';
import { useProvider, useProviderOpt } from '@utils/table/hooks/provider';
import { useQueryRef } from '@hooks/url';
import {
  startOfISOWeek,
  addDays,
  getISOWeek,
  getISOWeekYear,
  isBefore,
  format,
} from 'date-fns';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type { Region, VisibilityLocation } from '@/entities/Entities';

use([
  CustomChart,
  HeatmapChart,
  GridComponent,
  TooltipComponent,
  VisualMapComponent,
  CalendarComponent,
  CanvasRenderer,
]);

const region = ref<Region>();

const {
  data: regions,
  loading: loadingRegions,
} = useProviderOpt<Region>(
  '/regions',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value: Region) => ({
    label: value.name,
    value: value['@id'],
    gisLocation: value.gisLocation,
  }),
);

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);
const filter = computed(() => ({ region: region.value }));

const providerParams = reactive({
  sort: {
    sortKey: undefined,
    sortDirection: undefined,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const chartRef = ref(null);
const { data: visibilityData, loading } = useProvider<VisibilityLocation>('/visibility_locations/usage', providerParams);
const records = computed(() => unref(visibilityData)?.['hydra:member'] || []);

const locations = computed(() => [...new Set(records.value.map((loc) => loc.name))]);

const isoWeekArray = computed(() => {
  const startDate = addDays(new Date(), -14);
  const endDate = addDays(new Date(), 60);
  const isoWeeks = new Set();
  let currentDate = startOfISOWeek(startDate);

  while (isBefore(currentDate, endDate)) {
    const isoWeekNumber = getISOWeek(currentDate);
    const isoWeekYear = getISOWeekYear(currentDate);
    const isoWeekString = `${isoWeekYear}-W${String(isoWeekNumber).padStart(2, '0')}`;
    isoWeeks.add(isoWeekString);
    currentDate = addDays(currentDate, 7);
  }
  return Array.from(isoWeeks);
});

function parseISOWeek(isoWeekString: string): { start: Date, end: Date } {
  const [yearStr, weekStr] = isoWeekString.split('-W');
  const year = parseInt(yearStr, 10);
  const week = parseInt(weekStr, 10);

  const jan4 = new Date(year, 0, 4); // ISO week 1 always contains Jan 4
  const startOfFirstISOWeek = startOfISOWeek(jan4);
  const start = addDays(startOfFirstISOWeek, (week - 1) * 7);
  const end = addDays(start, 6);

  return { start, end };
}

// bring the data from the locations into a displayable form
const calendarData = computed(() => {
  if (!records.value) return [];

  const weekDayMap = new Map<string, Map<string, number>>(); // key = location-week → Map<day → totalBicycles>

  const fixedHour = 12; // Use 12:00:00

  for (const location of records.value) {
    for (const planning of location.visibilityPlannings) {
      if (!planning?.startTime || !planning?.endTime || planning.bicycleCount === undefined) continue;

      const planningStart = new Date(planning.startTime);
      const planningEnd = new Date(planning.endTime);

      for (
        let current = new Date(planningStart);
        current <= planningEnd;
        current = addDays(current, 1)
      ) {
        const probe = new Date(current);
        probe.setHours(fixedHour, 0, 0, 0); // Set to exact hour

        // Check if planning is active at this exact time
        if (planningStart <= probe && planningEnd >= probe) {
          const isoWeekYear = getISOWeekYear(probe);
          const isoWeekNumber = getISOWeek(probe);
          const weekString = `${isoWeekYear}-W${String(isoWeekNumber).padStart(2, '0')}`;

          const dayStr = format(probe, 'yyyy-MM-dd');
          const key = `${location.name}-${weekString}`;

          if (!weekDayMap.has(key)) {
            weekDayMap.set(key, new Map());
          }

          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const dayMap = weekDayMap.get(key)!;
          dayMap.set(dayStr, (dayMap.get(dayStr) ?? 0) + planning.bicycleCount);
        }
      }
    }
  }

  return Array.from(weekDayMap.entries()).map(([key, dayMap]) => {
    const [locName, ...weekParts] = key.split('-');
    const week = weekParts.join('-');
    const locIndex = locations.value.indexOf(locName);
    const maxCount = Math.max(...Array.from(dayMap.values()));
    return [week, locIndex, maxCount];
  });
});

const chartOptions = computed(() => ({
  tooltip: {
    formatter: (p) => {
      const weekStr = p.data[0];
      const { start, end } = parseISOWeek(weekStr);
      const startStr = format(start, 'dd.MM.yyyy');
      const endStr = format(end, 'dd.MM.yyyy');
      return `
        Standort: ${locations.value[p.data[1]]}<br/>
        Woche: ${weekStr}<br/>
        Start: ${startStr}<br/>
        Ende: ${endStr}<br/>
        Fahrräder: ${p.data[2]}
      `;
    },
  },
  yAxis: {
    type: 'category',
    data: locations.value,
    axisLabel: {
      rotate: 45,
      formatter: (value) => `{clickable|${value}}`,
      rich: {
        clickable: {
          color: '#007BFF',
          fontWeight: 'bold',
          fontSize: 12,
          textDecoration: 'underline',
        },
      },
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: '#e0e0e0',
        type: 'solid',
      },
    },
  },
  xAxis: {
    type: 'category',
    data: isoWeekArray.value.map((item) => item),
    axisLabel: {
      interval: 0,
      formatter: (value) => `{clickable|${value}}`,
      rich: {
        clickable: {
          color: '#007BFF',
          fontWeight: 'bold',
          fontSize: 12,
          textDecoration: 'underline',
        },
      },
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: '#e0e0e0',
        type: 'solid',
      },
    },
  },
  series: [
    {
      type: 'custom',
      renderItem: (params, api) => {
        const week = api.value(0);
        const locIndex = api.value(1);
        const count = api.value(2);

        const coord = api.coord([week, locIndex]);
        const size = api.size([1, 1]);

        const rectX = coord[0] - size[0] / 2;
        const rectY = coord[1] - size[1] / 2;

        const widthSize = size[0];
        const heightSize = size[1];

        const location = records.value.find((r: VisibilityLocation) => r.name === locations.value[locIndex]);

        const rectShape = {
          type: 'rect',
          shape: {
            x: rectX,
            y: rectY,
            width: widthSize,
            height: heightSize,
          },
          style: {
            fill: count > (location?.quota ?? 0) ? '#ff0000' : '#4CAF50',
            stroke: '#000',
            lineWidth: 1,
            cursor: 'pointer',
          },
        };
        const textShape = {
          type: 'text',
          style: {
            text: count.toString(),
            x: rectX + widthSize / 2,
            y: rectY + (heightSize / 2),
            fill: '#fff',
            textAlign: 'center',
            textVerticalAlign: 'middle',
            fontSize: 26,
            color: '#007BFF',
            fontWeight: 'bold',
          },
        };

        return {
          type: 'group',
          children: [rectShape, textShape],
        };
      },
      data: calendarData.value?.map((entry) => entry),
    },
  ],
}));
</script>

<style scoped>
button {
  @apply block;
}
</style>
